/**
 * XelantiIntro - the brand animation (approved v19 choreography).
 *
 * - Plays automatically on the very first app launch (localStorage flag),
 *   replayable any time from Settings -> Help & Support.
 * - Tagline is translated (intro.tagline, 28 locales) and follows the app
 *   language automatically because it goes through t().
 * - Tap anywhere to skip. Respects prefers-reduced-motion (simple fade).
 * - Rendered via createPortal to document.body so no page transform
 *   context can trap the fixed overlay.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "@/i18n";

const CSS = `
.xi-overlay{--xit:#111;position:fixed;inset:0;z-index:9999;background:var(--bg-primary,#FFF8F0);display:flex;
  align-items:center;justify-content:center;flex-direction:column;cursor:pointer;
  opacity:1;transition:opacity .35s ease}
.xi-overlay.xi-dark{--xit:#FFF}
.xi-overlay.xi-out{opacity:0}
.xi-scene{display:flex;flex-direction:column;align-items:center}
/* Reserve the same bottom space the standalone's Replay button occupies
   (controls block ~90px) so the in-flow logo/underline/tagline centre over
   (viewport - 90px) and rest ~45px above true centre, matching v36. The
   position:fixed cart is unaffected and stays at viewport centre. */
.xi-intro-spacer{flex:none;height:90px;pointer-events:none}
.xi-word{font-family:'DM Sans',sans-serif;font-size:clamp(48px,15vw,104px);font-weight:900;
  letter-spacing:-0.02em;color:var(--xit);display:flex;position:relative;line-height:1;perspective:900px}
.xi-xpos{display:inline-block;will-change:transform;transform-style:preserve-3d;opacity:0;transform:translate3d(0,0,-760px) scale(.06);transition:none}
.xi-xpos.xi-go{opacity:1}
.xi-measuring .xi-xpos{transform:none !important;transition:none}
.xi-xzoom{display:inline-block;transform-origin:50% 60%}
.xi-xspin{display:inline-block;transform-origin:50% 54%}
.xi-play .xi-xspin{animation:xi-spin 2.143s cubic-bezier(.3,.55,.2,1) 0s forwards}
@keyframes xi-spin{from{transform:rotateY(0deg)}to{transform:rotateY(720deg)}}
.xi-fly{opacity:0;display:inline-block;transform:translateX(65vw);transform-origin:left center}
.xi-measuring .xi-fly{transform:none !important}
.xi-play .xi-e{animation:xi-flyin .6s linear 1.843s forwards}
.xi-play .xi-l{animation:xi-flyin .6s linear 2.213s forwards}
.xi-play .xi-a{animation:xi-flyin .6s linear 2.583s forwards}
.xi-play .xi-n{animation:xi-flyin .6s linear 2.953s forwards}
.xi-play .xi-t{animation:xi-flyin .6s linear 3.323s forwards}
.xi-play .xi-i{animation:xi-flyin .6s linear 3.693s forwards}
@keyframes xi-flyin{
  0%{opacity:0;transform:translate(65vw,0) scale(1,1);animation-timing-function:cubic-bezier(.3,.1,.85,.9)}
  8%{opacity:1}
  58%{transform:translate(0,0) scale(1,1);animation-timing-function:cubic-bezier(0,.6,.4,1)}
  64%{transform:translate(0,0) scale(.5,1);animation-timing-function:cubic-bezier(.2,.8,.3,1.15)}
  100%{opacity:1;transform:translate(0,0) scale(1,1)}}
.xi-slot{position:relative;display:inline-block}
.xi-dot{position:absolute;width:.2em;height:.2em;
  left:50%;top:-.02em;margin-left:-.088em;opacity:0;transform:translate(0,-420px)}
.xi-dot svg{width:100%;height:100%;display:block}
.xi-play .xi-dot{animation:xi-bounce 2.25s linear 4.443s forwards}
@keyframes xi-bounce{
  0%{opacity:1;transform:translateY(-420px) scale(1,1);animation-timing-function:cubic-bezier(.55,0,1,.45)}
  11.8%{transform:translateY(0) scale(1.22,.72);animation-timing-function:cubic-bezier(0,.55,.45,1)}
  13.0%{transform:translateY(-8px) scale(.96,1.06);animation-timing-function:cubic-bezier(0,.55,.45,1)}
  28.1%{transform:translateY(-200px) scale(1,1);animation-timing-function:cubic-bezier(.55,0,1,.45)}
  44.4%{transform:translateY(0) scale(1.18,.76);animation-timing-function:cubic-bezier(0,.55,.45,1)}
  56.0%{transform:translateY(-100px) scale(1,1);animation-timing-function:cubic-bezier(.55,0,1,.45)}
  67.5%{transform:translateY(0) scale(1.14,.8);animation-timing-function:cubic-bezier(0,.55,.45,1)}
  75.5%{transform:translateY(-48px) scale(1,1);animation-timing-function:cubic-bezier(.55,0,1,.45)}
  83.5%{transform:translateY(0) scale(1.1,.85);animation-timing-function:cubic-bezier(0,.55,.45,1)}
  88.7%{transform:translateY(-20px) scale(1,1);animation-timing-function:cubic-bezier(.55,0,1,.45)}
  93.9%{transform:translateY(0) scale(1.06,.9);animation-timing-function:cubic-bezier(0,.55,.45,1)}
  96.9%{transform:translateY(-7px) scale(1,1);animation-timing-function:cubic-bezier(.55,0,1,.45)}
  100%{opacity:1;transform:translateY(0) scale(1,1)}}
.xi-line{height:.07em;background:var(--xit);border-radius:99px;width:100%;margin-top:.18em;
  transform:scaleX(0);transform-origin:left center;font-size:clamp(48px,15vw,104px)}
.xi-play .xi-line{animation:xi-drawline .5s cubic-bezier(.7,0,.3,1) 6.793s forwards}
@keyframes xi-drawline{to{transform:scaleX(1)}}
.xi-tag{font-family:'DM Sans',sans-serif;font-weight:700;font-size:clamp(15px,4.2vw,24px);
  color:var(--xit);margin-top:18px;opacity:0;transform:scale(.15);filter:blur(3px);z-index:-1;position:relative}
.xi-play .xi-tag{animation:xi-tagline 1.5s cubic-bezier(.3,.7,.3,1) 7.343s forwards}
@keyframes xi-tagline{
  0%{opacity:0;z-index:-1;transform:translateY(-1.4em) scale(.15);filter:blur(4px)}
  45%{opacity:1;z-index:5;transform:translateY(-.4em) scale(2);filter:blur(0)}
  100%{opacity:1;z-index:5;transform:translateY(0) scale(1);filter:blur(0)}}

.xi-cart{position:fixed;top:50%;left:50%;width:280px;height:280px;pointer-events:none;
  opacity:0;transform:translate(calc(-50% - 60vw), calc(-50% + 180px)) scale(.5)}
.xi-cart svg{width:100%;height:100%}
.xi-play .xi-cart{animation:xi-cartin 2.9s cubic-bezier(.2,.5,.4,1) 9.043s forwards}
@keyframes xi-cartin{
  0%{opacity:0;transform:translate(calc(-50% - 60vw), calc(-50% + 180px)) scale(.5);animation-timing-function:cubic-bezier(.2,.5,.4,1)}
  10%{opacity:.32}
  55%{opacity:.32;transform:translate(-50%, calc(-50% + 180px)) scale(.5);animation-timing-function:cubic-bezier(.3,.6,.2,1)}
  100%{opacity:.08;transform:translate(-50%,-50%) scale(1)}}
@media (prefers-reduced-motion: reduce){
  .xi-play .xi-zoom,.xi-play .xi-xela,.xi-play .xi-n,.xi-play .xi-t,.xi-play .xi-i,
  .xi-play .xi-dot,.xi-play .xi-line,.xi-play .xi-tag{animation:xi-rm-fade 1s ease forwards !important}
  .xi-play .xi-cart{animation:none !important;opacity:.08;transform:translate(-50%,-50%) scale(1)}
  .xi-fly{transform:none}
  @keyframes xi-rm-fade{from{opacity:0;transform:none}to{opacity:1;transform:none}}
}
`;


/* ------------------------------------------------------------------ blink
 * The ball blinks on every bounce impact, then twice more once it has settled.
 *
 * Lid geometry: each lid morphs between an OPEN path and a SHUT path whose
 * control points are flattened onto the centre line, while the two END points
 * are identical in both. Identical endpoints = pinned corners = a hinge. The
 * L92 -14 / L8 114 overshoot keeps the lid covering when shut; it is clipped to
 * the ball so it never shows.
 *
 * In this mark the seam IS the eyelid, so the seam travels with the lid. A seam
 * left behind while the lid closed would draw an open eye across a shut one.
 *
 * IMPACT TIMES are read off the real xi-bounce keyframes above: a 2.25s
 * animation delayed 4.443s, with squash frames at 11.8 / 44.4 / 67.5 / 83.5 /
 * 93.9 percent. Each blink begins 0.104s early - 40% of its 0.26s run - so the
 * eye is SHUT at the moment of the squash rather than starting to close then.
 *
 * The ball settles at 6.693s. The two settled double-blinks run at 7.0s and
 * 9.05s, finishing at 10.2s - before the tagline settles (~10.65s) and well
 * before the intro closes at TOTAL_MS. Nothing about the intro's length needed
 * to change.
 */
const XI_UP_OPEN = "M8 50 Q26 26 50 22 Q74 26 92 50 L92 -14 L8 -14 Z";
const XI_UP_SHUT = "M8 50 Q26 50 50 50 Q74 50 92 50 L92 -14 L8 -14 Z";
const XI_DN_OPEN = "M8 50 Q26 74 50 78 Q74 74 92 50 L92 114 L8 114 Z";
const XI_DN_SHUT = "M8 50 Q26 50 50 50 Q74 50 92 50 L92 114 L8 114 Z";
const XI_UE_OPEN = "M8 50 Q26 26 50 22 Q74 26 92 50";
const XI_UE_SHUT = "M8 50 Q26 50 50 50 Q74 50 92 50";
const XI_DE_OPEN = "M8 50 Q26 74 50 78 Q74 74 92 50";
const XI_DE_SHUT = XI_UE_SHUT;

const XI_IMPACTS = [4.709, 5.442, 5.962, 6.322, 6.556];
const XI_IMPACT_BEGIN = XI_IMPACTS.map((t) => `${(t - 0.104).toFixed(3)}s`).join(";");
const XI_SETTLED_BEGIN = "7s;9.05s";
const XI_SPLINES = Array(7).fill(".4 0 .2 1").join("; ");

const TOTAL_MS = 12600; // tagline settles ~10.65s; small grace then auto-close

export default function XelantiIntro({ onDone }) {
  // SMIL ignores prefers-reduced-motion entirely - the CSS @media block below
  // cannot stop it. The only way to honour the setting is to not render the
  // <animate> children at all.
  const [reducedMotion] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false);
  const { t } = useTranslation();
  const [out, setOut] = useState(false);
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const sceneRef = useRef(null);
  const wordRef = useRef(null);
  const xposRef = useRef(null);
  const doneRef = useRef(false);
  const startedRef = useRef(false);  // guard StrictMode double-invoke

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setOut(true);
    setTimeout(() => onDone && onDone(), 380);
  };

  const lidBlink = (open, shut) => {
    if (reducedMotion) return null;
    return (
      <>
        <animate attributeName="d" begin={XI_IMPACT_BEGIN} dur="0.26s" fill="freeze"
          calcMode="spline" keyTimes="0;.4;1" values={`${open};${shut};${open}`}
          keySplines=".4 0 .2 1;.4 0 .2 1" />
        <animate attributeName="d" begin={XI_SETTLED_BEGIN} dur="1.15s" fill="freeze"
          calcMode="spline" keyTimes="0;.13;.22;.31;.55;.64;.73;1"
          values={[open, open, shut, open, open, shut, open, open].join(";")}
          keySplines={XI_SPLINES} />
      </>
    );
  };

  useEffect(() => {
    if (startedRef.current) return;   // StrictMode mounts effects twice in dev
    startedRef.current = true;
    let timer;
    let glideTimer;
    let raf;
    const start = () => {
      // Hard reset: clear any transform a prior (double-invoked) run may have
      // left on the X, so the animation ALWAYS begins from the true start
      // state and never animates "from the seat backwards".
      try { if (xposRef.current) { xposRef.current.classList.remove("xi-go"); xposRef.current.style.transform = "translate3d(0,0,-760px) scale(.06)"; } } catch {}
      // X enters at the word's center (= screen center), then glides to
      // its seat. The glide is a JS-timed transition because WebKit doesn't
      // reliably resolve var() inside keyframe transforms.
      try {
        sceneRef.current.classList.add("xi-measuring");
        const wr = wordRef.current.getBoundingClientRect();
        const xr = xposRef.current.getBoundingClientRect();
        const dx = (wr.left + wr.width / 2) - (xr.left + xr.width / 2);
        sceneRef.current.classList.remove("xi-measuring");
        const xp = xposRef.current;
        // ONE continuous motion: the X emerges from deep at screen-center
        // (offset +dx, scaled down, pushed back in Z) and spirals forward
        // along a curved path to its seat - scale, depth and horizontal
        // sweep all interpolate together so it curves toward the spot
        // around the radius rather than arriving then sliding.
        const R = dx;                       // signed horizontal travel
        const DUR = 3000 / 1.4;
        const run = () => {
          const t0 = performance.now();
          const tick = (now) => {
            let p = (now - t0) / DUR;
            if (p > 1) p = 1;
            const e = 1 - Math.pow(1 - p, 2.4);          // ease-out over whole motion
            // Phase 1 (first 3/4): straight forward only - grows + comes
            // toward the front at its center start, NO sideways drift.
            // Phase 2 (last 1/4): curves sideways around to its seat.
            // The curve lives in the HORIZONTAL + DEPTH plane (the floor),
            // never vertical - the X stays on the same horizontal line the
            // whole time. It keeps coming FORWARD (z -> 0) while sweeping
            // SIDEWAYS (x: R -> 0) along a quarter-circle in that plane, so
            // it wraps around the imaginary ball as it arrives.
            // Quarter circle in the X-Z (horizontal-depth) plane, centred at
            // the seat. The X rides the rim: as the angle sweeps 0->90deg it
            // moves from "far to the side and deep" to "centre and full front",
            // so it keeps coming forward WHILE curving sideways around the
            // imaginary ball - and never leaves the horizontal line (y=0).
            // Speed TRANSFERS smoothly from the forward axis to the sideways
            // axis - that velocity crossover is what makes it read as a curve.
            // Phase 1 (first ~3/4): pure forward at constant speed, no sideways.
            // Phase 2: forward DECELERATES to a stop while sideways
            // ACCELERATES from zero (tangent-matched, so no kink), peaks at
            // mid-curve, then eases into the seat. Stays on the horizontal line.
            const a = Math.abs(R), sgn = Math.sign(R || 1);
            const DEPTH = 760, SPLIT = 0.7;
            let x, z;
            if (e < SPLIT) {
              const pe = e / SPLIT;                      // 0->1, linear = constant fwd speed
              x = R;
              z = -DEPTH * (1 - pe * 0.78);              // deep -> -167 (most of the way)
            } else {
              const q = (e - SPLIT) / (1 - SPLIT);       // 0->1
              // EXPONENTIAL speed change (not linear): sideways velocity ramps
              // up exponentially from zero, forward velocity decays
              // exponentially to zero - the classic ease curves. k sets how
              // sharp the exponential feels.
              const k = 4;
              // sideways: exponential ease-IN (slow start, accelerating hard)
              const sIn = (Math.exp(k * q) - 1) / (Math.exp(k) - 1);
              x = R * (1 - sIn);                         // R -> 0, accelerating
              // forward: exponential ease-OUT (fast then decaying to a stop)
              const fOut = 1 - Math.exp(-k * q);
              const zRem = -DEPTH * (1 - 0.78);
              z = zRem * (1 - fOut / (1 - Math.exp(-k))); // -167 -> 0, decelerating
            }
            const scale = 0.06 + 0.94 * (1 - (-z) / DEPTH);
            xp.style.transform =
              `translate3d(${x.toFixed(1)}px,0px,${z.toFixed(0)}px) scale(${scale.toFixed(3)})`;
            if (p < 1) raf = requestAnimationFrame(tick);
            else xp.style.transform = "translate3d(0,0,0) scale(1)";
          };
          raf = requestAnimationFrame(tick);
        };
        if (xp) xp.classList.add("xi-go");
        run();
      } catch {}
      sceneRef.current && sceneRef.current.classList.add("xi-play");
      const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      timer = setTimeout(finish, reduced ? 2200 : TOTAL_MS);
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => setTimeout(start, 150));
    } else {
      setTimeout(start, 400);
    }
    return () => { clearTimeout(timer); clearTimeout(glideTimer); if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <div className={`xi-overlay${isDark ? " xi-dark" : ""}${out ? " xi-out" : ""}`} onClick={finish} role="button" aria-label={t("common.skip")}>
      <style>{CSS}</style>
      <div className="xi-scene" ref={sceneRef}>
        <div className="xi-cart" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="#D4A574" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
        </div>
        {/* role+label: the wordmark spells the brand with a DOTLESS i
            (U+0131) so the logo ball can drop in as the dot - the v19
            choreography. Visually right, and to a screen reader it was
            the Turkish letter, so the brand was announced wrong and
            selecting the text copied something that does not match a
            search for "Xelanti". The letters are decoration; the label
            carries the name. */}
        <div className="xi-word" ref={wordRef} role="img" aria-label="Xelanti">
          <span className="xi-xpos" ref={xposRef}><span className="xi-xzoom"><span className="xi-xspin">X</span></span></span><span className="xi-fly xi-e">e</span><span className="xi-fly xi-l">l</span><span className="xi-fly xi-a">a</span><span className="xi-fly xi-n">n</span><span className="xi-fly xi-t">t</span><span className="xi-slot"><span className="xi-fly xi-i">ı</span><span className="xi-dot"><svg viewBox="0 0 100 100" aria-hidden="true"><defs><radialGradient id="xiG" cx="35" cy="28" r="78" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#FFC58A"/><stop offset="48%" stopColor="#F07A16"/><stop offset="100%" stopColor="#9B3603"/></radialGradient><filter id="xiC" x="-40%" y="-40%" width="180%" height="180%"><feDropShadow dx="1.2" dy="1.2" stdDeviation=".8" floodColor="#FFD9A8" floodOpacity=".7"/><feDropShadow dx="-1.2" dy="-1.2" stdDeviation="1" floodColor="#571C00" floodOpacity=".8"/></filter><linearGradient id="xiN" x1="20%" y1="0%" x2="80%" y2="100%"><stop offset="0%" stopColor="#FFF8F0"/><stop offset="100%" stopColor="#FFF5EB"/></linearGradient><filter id="xiK" x="-50%" y="-50%" width="200%" height="200%"><feDropShadow dx="1.1" dy="1.1" stdDeviation=".7" floodColor="#FFFFFF" floodOpacity=".9"/><feDropShadow dx="-1.1" dy="-1.1" stdDeviation=".9" floodColor="#8A4406" floodOpacity=".55"/></filter><clipPath id="xiP"><circle cx="50" cy="50" r="46"/></clipPath><clipPath id="xiL"><path d="M8 50 Q26 26 50 22 Q74 26 92 50 Q74 74 50 78 Q26 74 8 50 Z"/></clipPath></defs><circle cx="50" cy="50" r="46" fill="url(#xiG)"/><path d="M8 50 Q26 26 50 22 Q74 26 92 50 Q74 74 50 78 Q26 74 8 50 Z" fill="url(#xiN)"/><g clipPath="url(#xiL)"><path d="M8 50 Q26 26 50 22 Q74 26 92 50 Q74 74 50 78 Q26 74 8 50 Z" fill="none" stroke="#5B2200" strokeOpacity=".38" strokeWidth="7" transform="translate(1.6,2.2)"/><path d="M8 50 Q26 26 50 22 Q74 26 92 50 Q74 74 50 78 Q26 74 8 50 Z" fill="none" stroke="#FFE7C4" strokeOpacity=".25" strokeWidth="5" transform="translate(-1.4,-1.8)"/></g><g clipPath="url(#xiL)" filter="url(#xiK)" fill="none" stroke="#C2600C" strokeLinecap="round"><path d="M33 33 L67 67 M67 33 L33 67" strokeWidth="10"/></g><g clipPath="url(#xiP)"><path d={XI_UP_OPEN} fill="url(#xiG)">{lidBlink(XI_UP_OPEN, XI_UP_SHUT)}</path><path d={XI_DN_OPEN} fill="url(#xiG)">{lidBlink(XI_DN_OPEN, XI_DN_SHUT)}</path><g filter="url(#xiC)" fill="none" stroke="#B54F0A" strokeLinecap="round" strokeWidth="4" opacity=".85"><path d={XI_UE_OPEN}>{lidBlink(XI_UE_OPEN, XI_UE_SHUT)}</path><path d={XI_DE_OPEN}>{lidBlink(XI_DE_OPEN, XI_DE_SHUT)}</path></g></g></svg></span></span>
        </div>
        <div className="xi-line"></div>
        <div className="xi-tag">{t('intro.tagline')}</div>
      </div>
      <div className="xi-intro-spacer" aria-hidden="true"></div>
    </div>,
    document.body
  );
}
